import { createFeatureSelector, createSelector } from '@ngrx/store';

import * as RouterStoreSelectors from '../router/router.selector';

import * as fromReducer from './apple-profile-preset.reducer';
import { AppleProfilePresetState } from './apple-profile-preset.state';
import { AppleProfilePresetEntry, AppleProfilePresetType, PresetModelType } from './apple-profile-preset.types';

// ============================================================================
// BASE SELECTORS
// ============================================================================

export const selectAppleProfilePresetState = createFeatureSelector<AppleProfilePresetState>(
  fromReducer.APPLE_PROFILE_PRESET_FEATURE_KEY
);

export const selectAllAppleProfilePresets = createSelector(
  selectAppleProfilePresetState,
  fromReducer.selectAllAppleProfilePresets
);

export const selectAppleProfilePresetEntities = createSelector(
  selectAppleProfilePresetState,
  fromReducer.selectAppleProfilePresetEntities
);

export const selectAppleProfilePresetIds = createSelector(
  selectAppleProfilePresetState,
  fromReducer.selectAppleProfilePresetIds
);

// ============================================================================
// TYPE GUARDS
// ============================================================================

/**
 * Type guard to check if an entry matches the expected profile type.
 * This enables TypeScript to narrow the type without type casts.
 */
function isPresetEntryType<T extends AppleProfilePresetType>(
  profileType: T,
  entry: AppleProfilePresetEntry
): entry is AppleProfilePresetEntry<T> {
  return entry.profileType === profileType;
}

/**
 * Type guard to validate that a value is a valid AppleProfilePresetType.
 * This enables TypeScript to narrow the type without type casts.
 */
function isValidProfileType(value: unknown): value is AppleProfilePresetType {
  return (
    typeof value === 'string' &&
    (value === 'airplay-security' || value === 'restrictions-profile' || value === 'wifi-profile')
  );
}

// ============================================================================
// PARAMETERIZED SELECTORS
// ============================================================================

/**
 * Select all presets for a specific profile type.
 *
 * @example
 * ```typescript
 * this.store.select(selectPresetsByType('airplay-security'))
 * // Returns Observable<AppleAirPlaySecurityPreset[]>
 * ```
 */
export function selectPresetsByType<T extends AppleProfilePresetType>(profileType: T) {
  return createSelector(selectAllAppleProfilePresets, (entries): PresetModelType<T>[] =>
    entries
      .filter((entry): entry is AppleProfilePresetEntry<T> => isPresetEntryType(profileType, entry))
      .map((entry) => entry.preset)
  );
}

/**
 * Select a specific preset by profile type and preset ID.
 *
 * @example
 * ```typescript
 * this.store.select(selectPresetById('airplay-security', 'preset-123'))
 * // Returns Observable<AppleAirPlaySecurityPreset | undefined>
 * ```
 */
export function selectPresetById<T extends AppleProfilePresetType>(profileType: T, presetId: string) {
  return createSelector(selectAllAppleProfilePresets, (entries): PresetModelType<T> | undefined => {
    const matchingEntry = entries.find(
      (entry): entry is AppleProfilePresetEntry<T> => entry.profileType === profileType && entry.preset.id === presetId
    );
    return matchingEntry?.preset;
  });
}

/**
 * Select whether presets for a profile type are loaded (has at least one preset).
 *
 * @example
 * ```typescript
 * this.store.select(selectPresetsLoadedByType('wifi-profile'))
 * // Returns Observable<boolean>
 * ```
 */
export function selectPresetsLoadedByType(profileType: AppleProfilePresetType) {
  return createSelector(selectPresetsByType(profileType), (presets) => presets.length > 0);
}

// ============================================================================
// ROUTER-AWARE SELECTORS
// ============================================================================

/**
 * Select the profile type from the current route data.
 *
 * @example
 * ```typescript
 * this.store.select(selectProfileTypeFromRoute)
 * // Returns Observable<AppleProfilePresetType | undefined>
 * ```
 */
export const selectProfileTypeFromRoute = createSelector(
  RouterStoreSelectors.selectRouteDataValue('profileType'),
  (profileType): AppleProfilePresetType | undefined => {
    // Use type guard to narrow the type without casting
    if (isValidProfileType(profileType)) {
      return profileType;
    }
    return undefined;
  }
);
